import { AuditEvent } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/audit_pb';
import { Experiment } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_pb';
import { GetExperimentRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/experiment_service_pb';
import {
  Order,
  SortingRequest,
  SortProperty,
} from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/listing_pb';
import { ListProjectEventsRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/project_service_pb';
import { Property } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/query_pb';
import { RegisteredModel } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_pb';
import { GetRegisteredModelRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_service_pb';
import { RegisteredModelVersion } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_pb';
import { GetModelVersionRequest } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/registered_model_version_service_pb';
import { Tag } from '@buf/h2oai_mlops-storage.bufbuild_es/ai/h2o/mlops/storage/v1/tag_pb';
import { ExperimentService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/experiment_service_connect';
import { ProjectService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/project_service_connect';
import { RegisteredModelService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_service_connect';
import { RegisteredModelVersionService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/registered_model_version_service_connect';
import { TagService } from '@buf/h2oai_mlops-storage.connectrpc_es/ai/h2o/mlops/storage/v1/tag_service_connect';
import { createClient } from '@connectrpc/connect';
import { createConnectTransport } from '@connectrpc/connect-web';
import { IStyle, mergeStyles, MessageBarType } from '@fluentui/react';
import {
  BasicTable,
  Button,
  buttonStylesLink,
  IH2OTheme,
  Info,
  Link,
  linkStylesBlack,
  Loader,
  useClassNames,
  useTheme,
  useToast,
} from '@h2oai/ui-kit';
import React from 'react';
import { useHistory } from 'react-router-dom';

import { useUsers } from '../../authz/providers/UsersProvider';
import { DeploymentFixed } from '../../mlops/api';
import { useMLOpsService } from '../../mlops/hooks';
import { GrpcStatusCode, isGrpcGatewayErrorResponse } from '../../services/HTTPResponse';
import { useCloudPlatformDiscovery } from '../../utils/hooks';
import { ClassNamesFromIStyles } from '../../utils/models';
import { formatDefaultDateAndTime, formatError } from '../../utils/utils';
import { useWorkspaces } from '../Orchestrator/WorkspaceProvider';
import { ENDPOINTS, ROUTES } from './constants';

const notFoundModel = { displayName: 'not-found', id: 'not-found', name: 'not-found' };

const EVENTS_TABLE_HEIGHT = 408; // 9 rows of 48px - half of the row

interface IDashboardEventsWidgetStyles {
  header: IStyle;
  centerItems: IStyle;
  loaderWrapper: IStyle;
  loadMore: IStyle;
  loadMoreLoader: IStyle;
  mainContainer: IStyle;
}

const dashboardEventsWidgetStyles = (theme: IH2OTheme): IDashboardEventsWidgetStyles => {
  return {
    mainContainer: {
      overflowY: 'auto',
      width: '100%',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    centerItems: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      justifyContent: 'center',
      alignItems: 'center',
      height: '100%',
    },
    loaderWrapper: {
      height: 200,
    },
    loadMore: {
      display: 'flex',
      justifyContent: 'center',
      padding: '20px 0',
      borderWidth: 1,
      borderTopStyle: 'solid',
      borderColor: theme.semanticColors?.inputBorder,
      borderRadius: 4,
      // TODO: Use theme colors.
      backgroundImage: 'linear-gradient(to top, rgba(255,255,255,0), rgba(255,255,255, 1) 90%)',
      '&:hover': {
        borderColor: theme.semanticColors?.buttonPrimaryBackground,
        cursor: 'pointer',
        button: {
          textDecoration: 'none',
        },
        'button:active': {
          textDecoration: 'none',
        },
      },
      span: {
        color: theme.semanticColors?.textSecondary,
      },
      '&:hover span': {
        color: theme.semanticColors?.textPrimary,
      },
    },
    loadMoreLoader: {
      display: 'flex',
      justifyContent: 'center',
      padding: '10px 0',
    },
  };
};

type VersionWithModel = RegisteredModelVersion & { registeredModel: RegisteredModel };

export type EventItem = AuditEvent & {
  actorName?: string;
  createTime?: string;
};
interface TooltipContentProps {
  eventItem: EventItem;
}

const linkStyle = {
    color: 'var(--h2o-blue, #3B82F6)',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  titleStyle = { fontWeight: 600 };

const TooltipContent: React.FC<TooltipContentProps> = ({ eventItem }) => {
  const {
      experimentId,
      tagId,
      deploymentId,
      registeredModelId,
      registeredModelVersionId,
      // Properties for which details are not fetched.
      restrictionRoleId,
      commentId,
      datasetId,
    } = eventItem,
    { ACTIVE_WORKSPACE_NAME, ACTIVE_WORKSPACE_ID } = useWorkspaces(),
    history = useHistory(),
    mlopsService = useMLOpsService(),
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    storageTransport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}${ENDPOINTS.storage}/`,
    }),
    tagClient = createClient(TagService, storageTransport),
    experimentClient = createClient(ExperimentService, storageTransport),
    registeredModelClient = createClient(RegisteredModelService, storageTransport),
    registeredModelVersionClient = createClient(RegisteredModelVersionService, storageTransport),
    [loading, setLoading] = React.useState(false),
    loadStateRef = React.useRef({
      gettingExperiment: false,
      gettingTag: false,
      gettingDeployment: false,
      gettingRegisteredModel: false,
      gettingRegisteredModelVersion: false,
    }),
    evaluateLoading = () => {
      if (
        !loadStateRef.current.gettingExperiment &&
        !loadStateRef.current.gettingTag &&
        !loadStateRef.current.gettingDeployment &&
        !loadStateRef.current.gettingRegisteredModel &&
        !loadStateRef.current.gettingRegisteredModelVersion
      ) {
        setLoading(false);
      }
    },
    [experiment, setExperiment] = React.useState<Experiment | undefined>(undefined),
    [tag, setTag] = React.useState<Tag | undefined>(undefined),
    [deployment, setDeployment] = React.useState<DeploymentFixed | undefined>(undefined),
    [registeredModel, setRegisteredModel] = React.useState<RegisteredModel | undefined>(undefined),
    [registeredModelVersion, setRegisteredModelVersion] = React.useState<VersionWithModel | undefined>(undefined),
    isInfo =
      experiment ||
      datasetId ||
      tag ||
      commentId ||
      deployment ||
      restrictionRoleId ||
      registeredModel ||
      registeredModelVersion,
    getExperiment = React.useCallback(async (experimentId: string) => {
      setLoading(true);
      loadStateRef.current.gettingExperiment = true;
      try {
        const response = await experimentClient.getExperiment(
          new GetExperimentRequest({
            id: experimentId,
          })
        );
        const experiment = response?.experiment;
        if (!experiment) console.error('No experiment found in the response.');
        setExperiment(experiment);
      } catch (err) {
        const message = `Failed to fetch experiment metadata: ${formatError(err)}`;
        console.error(message);
        // TODO: Avoid error toast for the case when experiment is not found (e.g. it was deleted), but show it in other cases.
        // addToast({
        //   messageBarType: MessageBarType.error,
        //   message,
        // });
      } finally {
        loadStateRef.current.gettingExperiment = false;
        evaluateLoading();
      }
    }, []),
    getTag = React.useCallback(
      async (id: string) => {
        if (!ACTIVE_WORKSPACE_NAME || !id) return;
        setLoading(true);
        loadStateRef.current.gettingTag = true;
        try {
          const response = await tagClient.getTag({
            // TODO: Use ACTIVE_WORKSPACE_NAME directly when storage is migrated.
            projectId: ACTIVE_WORKSPACE_ID,
            id,
          });
          const tag = response?.tag;
          if (!tag) console.error('No tag found in the response.');
          setTag(tag);
        } catch (err) {
          const message = `Failed to fetch tag: ${formatError(err)}`;
          console.error(message);
        } finally {
          loadStateRef.current.gettingTag = false;
          evaluateLoading();
        }
      },
      [ACTIVE_WORKSPACE_ID]
    ),
    getDeployment = React.useCallback(
      async (name: string) => {
        if (!ACTIVE_WORKSPACE_NAME) return;
        setLoading(true);
        loadStateRef.current.gettingDeployment = true;
        try {
          const response = await mlopsService.getDeployment({ name });
          if (response && !response?.deployment) console.error('No deployments found in the response.');

          setDeployment(response?.deployment);
        } catch (err) {
          // If the deployment is not found.
          // Check if err has a 'code' property and it's 5 (Not Found)
          if (isGrpcGatewayErrorResponse(err) && err.code === GrpcStatusCode.NOT_FOUND) {
            setDeployment(notFoundModel as unknown as DeploymentFixed);
          } else {
            const message = `Failed to fetch deployments: ${formatError(err)}`;
            console.error(message);
            setDeployment(undefined);
          }
        } finally {
          loadStateRef.current.gettingDeployment = false;
          evaluateLoading();
        }
      },
      [ACTIVE_WORKSPACE_NAME]
    ),
    getRegisteredModel = React.useCallback(async (modelId: string) => {
      setLoading(true);
      loadStateRef.current.gettingRegisteredModel = true;
      try {
        const response = await registeredModelClient.getRegisteredModel(new GetRegisteredModelRequest({ modelId }));
        const model = response?.registeredModel;
        if (!model) {
          console.error('No model found in the response.');
        }
        if (model?.status === 'DELETED') {
          setRegisteredModel(notFoundModel as unknown as RegisteredModel);
        } else {
          setRegisteredModel(model);
        }
      } catch (err) {
        // If the model is not found.
        // Check if err has a 'code' property and it's 5 (Not Found)
        if (isGrpcGatewayErrorResponse(err) && err.code === GrpcStatusCode.NOT_FOUND) {
          setRegisteredModel(notFoundModel as unknown as RegisteredModel);
        } else {
          const message = `Failed to fetch model data: ${formatError(err)}`;
          console.error(message);
          setRegisteredModel(undefined);
        }
      } finally {
        loadStateRef.current.gettingRegisteredModel = false;
        evaluateLoading();
      }
    }, []),
    getRegisteredModelVersion = React.useCallback(async (modelVersionId: string) => {
      setLoading(true);
      loadStateRef.current.gettingRegisteredModelVersion = true;
      try {
        const getModelVersionRequest = new GetModelVersionRequest({
          modelVersionId,
        });
        const response = await registeredModelVersionClient.getModelVersion(getModelVersionRequest);
        const modelVersion = response?.registeredModelVersion;
        if (modelVersion) {
          const response = await registeredModelClient.getRegisteredModel(
            new GetRegisteredModelRequest({ modelId: modelVersion.registeredModelId })
          );
          const model = response?.registeredModel;
          if (!model) {
            console.error('No model found in the response.');
          } else {
            setRegisteredModelVersion({ ...modelVersion, registeredModel: model } as VersionWithModel);
          }
        } else {
          console.error('No model version found in the response.');
        }
      } catch (err) {
        // If the model version is not found.
        // Check if err has a 'code' property and it's 5 (Not Found)
        if (isGrpcGatewayErrorResponse(err) && err.code === GrpcStatusCode.NOT_FOUND) {
          setRegisteredModelVersion({
            id: 'not-found',
            registeredModelId: 'not-found',
            registeredModel: notFoundModel,
          } as unknown as VersionWithModel);
        } else {
          const message = `Failed to fetch model version ${modelVersionId}: ${formatError(err)}`;
          console.error(message);
          setRegisteredModelVersion(undefined);
        }
      } finally {
        loadStateRef.current.gettingRegisteredModelVersion = false;
        evaluateLoading();
      }
    }, []);

  React.useEffect(() => {
    if (experimentId) {
      getExperiment(experimentId);
    }
  }, [experimentId, getExperiment]);

  React.useEffect(() => {
    if (tagId) {
      getTag(tagId);
    }
  }, [tagId, getTag]);

  React.useEffect(() => {
    if (deploymentId && ACTIVE_WORKSPACE_NAME) {
      getDeployment(`${ACTIVE_WORKSPACE_NAME}${ROUTES.DEPLOYMENTS}/${deploymentId}`);
    }
  }, [deploymentId, ACTIVE_WORKSPACE_NAME, getDeployment]);

  React.useEffect(() => {
    if (registeredModelId) {
      getRegisteredModel(registeredModelId);
    }
  }, [registeredModelId, getRegisteredModel]);

  React.useEffect(() => {
    if (registeredModelVersionId) {
      getRegisteredModelVersion(registeredModelVersionId);
    }
  }, [registeredModelVersionId, getRegisteredModelVersion]);

  return loading ? (
    <Loader />
  ) : isInfo ? (
    <div>
      {experiment && (
        <p>
          <span style={titleStyle}>Experiment:</span> {experiment.displayName || experimentId}
        </p>
      )}
      {datasetId && (
        <p>
          <span style={titleStyle}>Dataset Id:</span> {datasetId}
        </p>
      )}
      {tag && (
        <p>
          <span style={titleStyle}>Tag:</span> {tag.displayName}
        </p>
      )}
      {commentId && (
        <p>
          <span style={titleStyle}>Comment Id:</span> {commentId}
        </p>
      )}
      {deployment && (
        <p>
          <span style={titleStyle}>Deployment:</span>{' '}
          <span
            onClick={
              deployment.displayName !== notFoundModel.displayName
                ? () => history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.DEPLOYMENTS}/${deploymentId}`)
                : undefined
            }
            style={deployment.displayName !== notFoundModel.displayName ? linkStyle : undefined}
          >
            {deployment.displayName || deploymentId}
          </span>
        </p>
      )}
      {restrictionRoleId && (
        <p>
          <span style={titleStyle}>Restriction Role Id:</span> {restrictionRoleId}
        </p>
      )}
      {registeredModel && (
        <p>
          <span style={titleStyle}>Registered Model:</span>{' '}
          <span
            onClick={
              registeredModel.displayName !== notFoundModel.displayName
                ? () => history.push(`/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.MODELS}/${registeredModelId}`)
                : undefined
            }
            style={registeredModel.displayName !== notFoundModel.displayName ? linkStyle : undefined}
          >
            {registeredModel.displayName || registeredModelId}
          </span>
        </p>
      )}
      {registeredModelVersion && (
        <p>
          Registered model version {registeredModelVersion.version} for model{' '}
          <span
            onClick={() =>
              registeredModelVersion.registeredModel?.displayName !== notFoundModel.displayName
                ? history.push(
                    `/mlops/${ACTIVE_WORKSPACE_NAME}${ROUTES.MODELS}/${registeredModelVersion.registeredModel?.id}`
                  )
                : undefined
            }
            style={
              registeredModelVersion.registeredModel?.displayName !== notFoundModel.displayName ? linkStyle : undefined
            }
          >
            {registeredModelVersion.registeredModel?.displayName}
          </span>
        </p>
      )}
    </div>
  ) : (
    <div>N/A</div>
  );
};

const DashboardEventsWidget = () => {
  const theme = useTheme(),
    classNames = useClassNames<IDashboardEventsWidgetStyles, ClassNamesFromIStyles<IDashboardEventsWidgetStyles>>(
      'dashboardEventsWidget',
      dashboardEventsWidgetStyles(theme)
    ),
    { addToast } = useToast(),
    { usersNameMap } = useUsers(),
    { ACTIVE_WORKSPACE_NAME } = useWorkspaces(),
    [events, setEvents] = React.useState<AuditEvent[]>([]),
    [loadingEvents, setLoadingEvents] = React.useState(false),
    [nextPageTokenEvents, setNextPageTokenEvents] = React.useState<Uint8Array>(),
    [isLoadingMoreEvents, setIsLoadingMoreEvents] = React.useState(false),
    moreEventsAvailable = !!nextPageTokenEvents,
    // TODO: Move to context global to mlops.
    cloudPlatformDiscovery = useCloudPlatformDiscovery(),
    mlopsApiUrl = cloudPlatformDiscovery?.mlopsApiUrl || '',
    transport = createConnectTransport({
      baseUrl: `${mlopsApiUrl}/storage/`,
    }),
    projectClient = createClient(ProjectService, transport),
    [eventsOrderDesc, setEventsOrderDesc] = React.useState(true),
    listEvents = React.useCallback(
      async (pageToken?: Uint8Array, newestFirst = true) => {
        if (!ACTIVE_WORKSPACE_NAME) return;
        if (pageToken) {
          setIsLoadingMoreEvents(true);
        } else {
          setLoadingEvents(true);
        }
        const request = new ListProjectEventsRequest({
          projectId: ACTIVE_WORKSPACE_NAME.replace('workspaces/', ''),
          paging: {
            pageSize: 12,
            pageToken,
          },
          sorting: new SortingRequest({
            property: [
              new SortProperty({
                property: new Property({
                  propertyType: {
                    case: 'field',
                    value: 'timestamp',
                  },
                }),
                order: newestFirst ? Order.DESCENDING : Order.ASCENDING,
              }),
            ],
          }),
        });
        try {
          const response = await projectClient.listProjectEvents(request);
          setNextPageTokenEvents(response?.paging?.nextPageToken);
          if (response) {
            const newEvents = response?.event?.map(
              (e) =>
                ({
                  ...e,
                  actorName: usersNameMap?.[`users/${e.actorId}`]?.displayName || e.actorId,
                  createTime:
                    e?.timestamp?.seconds !== undefined
                      ? formatDefaultDateAndTime(Number(e.timestamp.seconds) * 1000)
                      : '',
                } as EventItem)
            );
            setEvents((events) => (pageToken ? [...(events || []), ...(newEvents || [])] : newEvents));
          }
        } catch (err) {
          const message = `Error listing events: ${formatError(err)}`;
          console.error(message);
          addToast({
            messageBarType: MessageBarType.error,
            message,
          });
        } finally {
          setLoadingEvents(false);
          setIsLoadingMoreEvents(false);
        }
      },
      [addToast, ACTIVE_WORKSPACE_NAME]
    ),
    onLoadMoreEvents = React.useCallback(
      (orderDesc = true) => {
        if (nextPageTokenEvents) {
          listEvents(nextPageTokenEvents, orderDesc);
        }
      },
      [nextPageTokenEvents, listEvents]
    );

  React.useEffect(() => {
    if (ACTIVE_WORKSPACE_NAME) listEvents();
  }, [ACTIVE_WORKSPACE_NAME]);

  return (
    <div className={classNames.mainContainer}>
      <div className={classNames.header}>
        <h3>MLOps events</h3>
        {!loadingEvents ? (
          <div>
            <Button
              text={eventsOrderDesc ? 'Newest first' : 'Oldest first'}
              styles={[
                buttonStylesLink,
                {
                  rootExpanded: { borderColor: 'transparent', backgroundColor: 'transparent' },
                  rootDisabled: {
                    background: 'transparent',
                    borderColor: 'transparent',
                  },
                },
              ]}
              disabled={!events?.length}
              menuProps={{
                items: eventsOrderDesc
                  ? [
                      {
                        key: 'oldest-first',
                        text: 'Oldest first',
                        onClick: () => {
                          setEventsOrderDesc(false);
                          listEvents(undefined, false);
                        },
                      },
                    ]
                  : [
                      {
                        key: 'newest-first',
                        text: 'Newest first',
                        onClick: () => {
                          setEventsOrderDesc(true);
                          listEvents(undefined, true);
                        },
                      },
                    ],
                calloutProps: {
                  styles: {
                    root: {
                      padding: '8px 0px',
                    },
                  },
                },
              }}
            />
          </div>
        ) : null}
      </div>
      {loadingEvents ? (
        <div className={mergeStyles(classNames.centerItems, classNames.loaderWrapper)}>
          <Loader />
        </div>
      ) : (
        <>
          <BasicTable
            columns={[
              {
                fieldName: 'createTime',
                key: 'createTime',
                minWidth: 180,
                maxWidth: 200,
                name: 'Time',
              },
              {
                fieldName: 'message',
                key: 'message',
                minWidth: 220,
                name: 'Event message',
                data: {
                  listCellProps: {
                    emptyMessage: '',
                    onRenderText: (item: EventItem) => (
                      <div
                        style={{
                          display: 'flex',
                        }}
                      >
                        <div
                          style={{
                            color: 'var(--h2o-gray500, #6B7280)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {item?.message}
                        </div>
                        <Info
                          isTooltip
                          styles={{
                            icon: {
                              paddingTop: 0,
                            },
                            root: {
                              marginLeft: 5,
                            },
                          }}
                        >
                          <TooltipContent eventItem={item} />
                        </Info>
                      </div>
                    ),
                  },
                },
              },
              {
                fieldName: 'actorName',
                key: 'actorName',
                minWidth: 120,
                name: 'Actor',
              },
            ]}
            items={events}
            styles={{
              rows: {
                maxHeight: EVENTS_TABLE_HEIGHT,
                overflowY: 'auto',
              },
              root: {
                borderRadius: 4,
              },
            }}
          />
          {moreEventsAvailable ? (
            isLoadingMoreEvents ? (
              <Loader className={classNames.loadMoreLoader} />
            ) : (
              <div className={classNames.loadMore} onClick={() => onLoadMoreEvents?.(eventsOrderDesc)}>
                <Link styles={linkStylesBlack}>Load more events</Link>
              </div>
            )
          ) : (
            <div style={{ height: 59 }} />
          )}
        </>
      )}
    </div>
  );
};
export default DashboardEventsWidget;
